/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API Telemetry & Analytics Dashboard (Task 10.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  Calendar,
  RefreshCw,
  Activity,
  Zap,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useDeveloperAnalyticsStore,
  TimeRange,
} from '../../stores/useDeveloperAnalyticsStore';
import { AnalyticsSummaryCards } from './analytics/AnalyticsSummaryCards';
import { RequestVolumeChart } from './analytics/RequestVolumeChart';
import { HttpStatusDonutChart } from './analytics/HttpStatusDonutChart';
import { EndpointUsageBarChart } from './analytics/EndpointUsageBarChart';
import { RecentApiLogsTable } from './analytics/RecentApiLogsTable';

export const DeveloperAnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const {
    timeRange,
    isRefreshing,
    setTimeRange,
    refreshData,
  } = useDeveloperAnalyticsStore();

  const timeRanges: { id: TimeRange; label: string }[] = [
    { id: '24h', label: t('apikeys.timeRange24h') },
    { id: '7d', label: t('apikeys.timeRange7d') },
    { id: '30d', label: t('apikeys.timeRange30d') },
    { id: '90d', label: t('apikeys.timeRange90d') },
  ];

  return (
    <div className="space-y-6">
      {/* Dashboard Sub-Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl bg-surface border border-surface-border">
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
            <Activity className="size-4.5" />
          </div>
          <div>
            <h2 className="text-sm font-bold font-heading text-foreground">
              {t('apikeys.analyticsTitle')}
            </h2>
            <p className="text-[11px] text-foreground-muted">
              {t('apikeys.analyticsSubtitle')}
            </p>
          </div>
        </div>

        {/* Time Range Selector & Refresh Button */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {/* Time range pills */}
          <div className="p-0.5 rounded-xl bg-surface-elevated border border-surface-border flex items-center gap-1">
            <Calendar className="size-3.5 text-foreground-muted ml-2 mr-1" />
            {timeRanges.map((range) => (
              <button
                key={range.id}
                onClick={() => setTimeRange(range.id)}
                className={`px-2.5 py-1.5 rounded-lg text-xs transition-all ${
                  timeRange === range.id
                    ? 'bg-primary text-primary-fg font-bold shadow-xs'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>

          {/* Refresh button */}
          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className="p-2 rounded-xl border border-surface-border bg-surface-elevated hover:bg-surface-subtle text-foreground-muted hover:text-foreground transition-all shadow-xs active:scale-95 disabled:opacity-50"
            title={t('apikeys.refresh')}
          >
            <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin text-primary' : ''}`} />
          </button>
        </div>
      </div>

      {/* 1. Metric Summary KPI Cards */}
      <AnalyticsSummaryCards />

      {/* 2. Request Volume Area Chart */}
      <RequestVolumeChart />

      {/* 3. HTTP Status Donut & Endpoint Breakdown Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HttpStatusDonutChart />
        <EndpointUsageBarChart />
      </div>

      {/* 4. Recent API Logs Table */}
      <RecentApiLogsTable />

      {/* Edge Telemetry Infrastructure Callout */}
      <div className="p-4 rounded-2xl border border-surface-border bg-surface/50 backdrop-blur-sm flex items-center justify-between gap-4 text-xs">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Zap className="size-4" />
          </div>
          <p className="text-[11px] text-foreground-muted">
            Telemetry metrics are aggregated directly via Cloudflare Workers Analytics Engine and distributed edge log streaming with zero impact on request latency.
          </p>
        </div>
      </div>
    </div>
  );
};
